import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  deleteSettingsTemplate,
  getSettingsTemplate,
  getSettingsTemplates,
  updateSettingsTemplate,
  validateSettingsTemplateSource,
} from '../api/alertApi';
import ConfirmDialog from '../components/ConfirmDialog';
import TemplateCreateModal from '../components/TemplateCreateModal';
import TemplateTestPanel from '../components/TemplateTestPanel';
import TemplateWizardFields, { card, primaryButton, secondaryButton, sectionTitle } from '../components/TemplateWizardFields';
import { asText, buildPayload, emptyTemplate, normalizeDetail, parseJson } from '../utils/templateForm';

export default function AlertSettings() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyTemplate);
  const [activeStep, setActiveStep] = useState('basics');
  const [sourceTestDeviceId, setSourceTestDeviceId] = useState('');
  const [sourceValidationResult, setSourceValidationResult] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const templatesQuery = useQuery({
    queryKey: ['settings-templates'],
    queryFn: getSettingsTemplates,
  });

  const detailQuery = useQuery({
    queryKey: ['settings-template', selectedId],
    queryFn: () => getSettingsTemplate(selectedId),
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (!selectedId && templatesQuery.data?.data?.length) {
      setSelectedId(templatesQuery.data.data[0].id);
    }
  }, [selectedId, templatesQuery.data]);

  useEffect(() => {
    if (detailQuery.data?.data) {
      const next = normalizeDetail(detailQuery.data.data);
      setForm(next);
      setSourceValidationResult(null);
      setActiveStep('basics');
    }
  }, [detailQuery.data]);

  const templates = templatesQuery.data?.data || [];
  const inactiveCount = templates.filter((t) => !t.is_active).length;
  const visibleTemplates = showInactive ? templates : templates.filter((t) => t.is_active);

  function selectTemplate(id) {
    setSelectedId(id);
  }

  function handleCreated(newTemplate) {
    qc.invalidateQueries({ queryKey: ['settings-templates'] });
    setShowCreateModal(false);
    setSelectedId(newTemplate.id);
    qc.setQueryData(['settings-template', newTemplate.id], { data: newTemplate });
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(form);
      if (!payload.name) throw new Error('Template name is required');
      if (payload.source_type === 'http' && !payload.data_source_url) {
        throw new Error('Data source URL is required');
      }
      if (payload.source_type === 'mongodb' && (!payload.mongodb_uri || !payload.mongodb_database || !payload.mongodb_collection)) {
        throw new Error('MongoDB connection string, database, and collection are required');
      }
      return updateSettingsTemplate(selectedId, payload);
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['settings-templates'] });
      if (result?.data?.id) {
        setForm(normalizeDetail(result.data));
        qc.setQueryData(['settings-template', result.data.id], result);
      }
      toast.success('Template saved');
    },
    onError: (err) => toast.error(err.message || err.response?.data?.error || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSettingsTemplate(selectedId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings-templates'] });
      setSelectedId(null);
      setForm(emptyTemplate());
      setSourceValidationResult(null);
      setSourceTestDeviceId('');
      setShowDeleteConfirm(false);
      toast.success('Template deleted');
    },
    onError: (err) => {
      setShowDeleteConfirm(false);
      toast.error(err.response?.data?.error || 'Delete failed', { duration: 6000 });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: () => updateSettingsTemplate(selectedId, buildPayload({ ...form, is_active: !form.is_active })),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['settings-templates'] });
      if (result?.data) {
        setForm(normalizeDetail(result.data));
        qc.setQueryData(['settings-template', result.data.id], result);
      }
      toast.success(form.is_active ? 'Template disabled' : 'Template enabled');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Update failed'),
  });

  const validateSourceMutation = useMutation({
    mutationFn: () => validateSettingsTemplateSource({
      source_type: form.source_type,
      data_source_url: asText(form.data_source_url).trim(),
      method: form.method,
      headers: parseJson(form.headersText, 'Headers'),
      query_params: parseJson(form.queryParamsText, 'Query params'),
      request_body: parseJson(form.requestBodyText, 'Request body'),
      mongodb_uri: asText(form.mongodb_uri).trim(),
      mongodb_database: asText(form.mongodb_database).trim(),
      mongodb_collection: asText(form.mongodb_collection).trim(),
      mongodb_filter: parseJson(form.mongodb_filterText, 'MongoDB filter'),
      mongodb_sort_field: asText(form.mongodb_sort_field).trim() || '_id',
      variable_mapping: {},
      sample_device_id: asText(sourceTestDeviceId).trim(),
    }),
    onSuccess: (result) => {
      setSourceValidationResult(result);
      toast.success(result.success ? 'Source check completed' : 'Source check returned an error');
    },
    onError: (err) => toast.error(err.message || err.response?.data?.error || 'Source check failed'),
  });

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateVariable(index, key, value) {
    setForm((current) => ({
      ...current,
      variableRows: current.variableRows.map((row, idx) => idx === index ? { ...row, [key]: value } : row),
    }));
  }

  function addVariable() {
    setForm((current) => ({
      ...current,
      variableRows: [...current.variableRows, { key: '', path: '', format: 'none', timestampFormat: 'YYYY-MM-DD HH:mm:ss', timezone: '' }],
    }));
  }

  function removeVariable(index) {
    setForm((current) => ({
      ...current,
      variableRows: current.variableRows.length === 1
        ? [{ key: '', path: '', format: 'none', timestampFormat: 'YYYY-MM-DD HH:mm:ss', timezone: '' }]
        : current.variableRows.filter((_, idx) => idx !== index),
    }));
  }

  const selectedTemplateMeta = templates.find((t) => t.id === selectedId);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr)', gap: 24 }}>
      <aside style={sidebar}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Template Settings</div>
          <div style={{ color: '#64748b', fontSize: 13 }}>Reusable use-cases</div>
        </div>

        <button style={newTemplateButton} onClick={() => setShowCreateModal(true)}>
          + New Template
        </button>

        <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
          {visibleTemplates.map((template) => (
            <button
              key={template.id}
              onClick={() => selectTemplate(template.id)}
              style={{
                ...listItem,
                borderColor: selectedId === template.id ? '#0284c7' : '#dbe4f0',
                background: selectedId === template.id ? '#eff6ff' : '#ffffff',
                opacity: template.is_active ? 1 : 0.65,
              }}
            >
              <div style={{ fontWeight: 700, color: '#0f172a', textAlign: 'left' }}>{template.name}</div>
              <div style={{ marginTop: 4 }}>
                <span style={{
                  ...miniPill,
                  background: template.is_active ? '#dcfce7' : '#f3f4f6',
                  color: template.is_active ? '#166534' : '#6b7280',
                }}>
                  {template.is_active ? 'Active' : 'Disabled'}
                </span>
              </div>
            </button>
          ))}
          {!visibleTemplates.length && (
            <div style={{ ...card, color: '#64748b', fontSize: 14 }}>
              {templates.length ? 'No active templates. Show disabled ones below.' : 'No templates yet — create the first one above.'}
            </div>
          )}
        </div>

        {inactiveCount > 0 && (
          <button
            style={linkButton}
            onClick={() => setShowInactive((current) => !current)}
          >
            {showInactive ? 'Hide' : 'Show'} disabled templates ({inactiveCount})
          </button>
        )}
      </aside>

      <section style={{ display: 'grid', gap: 18 }}>
        {selectedTemplateMeta ? (
          <>
            <div style={hero}>
              <div>
                <h2 style={{ margin: 0, fontSize: 26, color: '#0f172a' }}>{form.name || selectedTemplateMeta.name}</h2>
                <p style={{ margin: '8px 0 0', color: '#475569', maxWidth: 760 }}>
                  Editing an existing template. Save your changes from the button at the bottom of
                  step 3.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  style={secondaryButton}
                  onClick={() => toggleActiveMutation.mutate()}
                  disabled={toggleActiveMutation.isPending}
                >
                  {form.is_active ? 'Disable' : 'Enable'}
                </button>
                <button style={dangerLinkButton} onClick={() => setShowDeleteConfirm(true)} disabled={deleteMutation.isPending}>
                  Delete
                </button>
              </div>
            </div>

            <TemplateWizardFields
              form={form}
              updateField={updateField}
              updateVariable={updateVariable}
              addVariable={addVariable}
              removeVariable={removeVariable}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              sourceTestDeviceId={sourceTestDeviceId}
              setSourceTestDeviceId={setSourceTestDeviceId}
              sourceValidationResult={sourceValidationResult}
              onValidateSource={() => validateSourceMutation.mutate()}
              validatingSource={validateSourceMutation.isPending}
              onSave={() => saveMutation.mutate()}
              saving={saveMutation.isPending}
              saveLabel="Save Changes"
            />

            <TemplateTestPanel templateId={selectedId} />
          </>
        ) : (
          <div style={{ ...card, textAlign: 'center', padding: 48 }}>
            <div style={sectionTitle}>No template selected</div>
            <p style={{ color: '#64748b', maxWidth: 480, margin: '0 auto 18px' }}>
              Select a template from the list on the left to edit it, or create a new one to get
              started.
            </p>
            <button style={primaryButton} onClick={() => setShowCreateModal(true)}>
              + New Template
            </button>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete this template?"
        message={`"${form.name || 'This template'}" will be permanently deleted. This can't be undone. If any device mapping in the Devices module still uses it, deletion will be blocked until that mapping is removed or reassigned — consider Disable instead if you just want to stop using it for now.`}
        confirmLabel="Delete Template"
        danger
        busy={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {showCreateModal && (
        <TemplateCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}

const sidebar = {
  alignSelf: 'start',
  position: 'sticky',
  top: 24,
  background: '#f8fbff',
  border: '1px solid #dbe4f0',
  borderRadius: 20,
  padding: 18,
};

const hero = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'start',
  gap: 16,
  padding: 24,
  borderRadius: 24,
  border: '1px solid #dbe4f0',
  background: 'linear-gradient(135deg, #fff8ec 0%, #eef8ff 100%)',
};

const listItem = {
  border: '1px solid #dbe4f0',
  borderRadius: 16,
  padding: 14,
  cursor: 'pointer',
  transition: 'background 0.15s ease, border-color 0.15s ease',
};

const miniPill = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 700,
};

const linkButton = {
  border: 'none',
  background: 'none',
  color: '#0284c7',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  padding: '4px 0',
  textAlign: 'left',
  marginTop: 14,
};

const dangerLinkButton = {
  border: '1px solid #fecaca',
  borderRadius: 999,
  padding: '10px 18px',
  background: '#fff5f5',
  color: '#dc2626',
  fontWeight: 700,
  cursor: 'pointer',
};

const newTemplateButton = {
  width: '100%',
  border: 'none',
  borderRadius: 14,
  padding: '12px 16px',
  background: '#0f766e',
  color: '#fff',
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: 14,
};
